import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Box,
  Typography,
  Button,
  IconButton,
  Tabs,
  Tab,
  Breadcrumbs,
  Link,
  Chip,
  TextField,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Zoom,
  Fade,
  Paper,
  Skeleton,
  Alert,
  Snackbar,
  Collapse,
  keyframes,
} from '@mui/material';
import {
  FavoriteBorder as FavoriteIcon,
  Favorite as FavoriteFilledIcon,
  Share as ShareIcon,
  Remove as RemoveIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Info as InfoIcon,
  ShoppingBag as BagIcon,
  ZoomIn as ZoomInIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import RenderBlock from '../../components/customer/links/renderBlock';
import { addToCart } from '../../features/cartSlice';
import { productsApi } from '../api/products';
import { cartApi } from '../api/cart';
import { profileApi } from '../api/profile';
import { personalizationService } from '../services/personalization';
import packageService from '../services/packageService';
import recommendationsService from '../services/recommendationsService';
import SmartRecommendations from '../components/SmartRecommendations';
import placeholderImage from '../../images/No-Image-Placeholder.png';
import secureLocalStorage from 'react-secure-storage';
import accountService from '../services/accountService';
import Seo from '../components/Seo';

// Shimmer animation for loading skeletons
const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// Apply background style to body immediately
if (typeof document !== 'undefined' && document.body) {
  document.body.style.backgroundColor = '#f8f9fa';
}

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const disclaimer = useSelector((state) => state.template?.disclaimer);

  // Merchant policies are stored as draft.js raw JSON; null when empty/invalid
  const parseDraftContent = (raw) => {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (!parsed?.blocks?.some((block) => block.text?.trim())) return null;
      return parsed;
    } catch (e) {
      return null;
    }
  };

  // Product data state - Initialize loading states to true to show skeleton immediately
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true); // Start with loading true
  const [error, setError] = useState(null);
  const [initialLoad, setInitialLoad] = useState(true); // Always true on mount

  // Variation state - similar to old product page
  const [variations, setVariations] = useState({});
  const [variationKey, setVariationKey] = useState('');
  const [variationId, setVariationId] = useState('');
  const [price, setPrice] = useState(0);
  const [normalPrice, setNormalPrice] = useState(0);
  const [discountedPrice, setDiscountedPrice] = useState(0);
  const [stock, setStock] = useState(0);
  const [, setVariationsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Start with loading true

  // UI state
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [, setImageErrors] = useState({});

  // Template compatibility state
  const [productVariants, setProductVariants] = useState([]);
  const [showVariantsSummary, setShowVariantsSummary] = useState(true);
  const [showVariantAdded] = useState(false);

  // Recommendations state
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);

  // Customization state
  const [enablePrintName, setEnablePrintName] = useState(false);
  const [enablePrintNumber, setEnablePrintNumber] = useState(false);
  const [customPrintName, setCustomPrintName] = useState('');
  const [customPrintNumber, setCustomPrintNumber] = useState('');
  const [selectedFontType, setSelectedFontType] = useState('LEAGUE FONT');
  const [selectedBadge, setSelectedBadge] = useState('');
  const [selectedAdditionalServices, setSelectedAdditionalServices] = useState([]);
  const [additionalServicesAmount, setAdditionalServicesAmount] = useState(0);
  const [customizationEditMode, setCustomizationEditMode] = useState({});
  const [customizationValues, setCustomizationValues] = useState({});

  // Other UI state
  const [showAddedToCart, setShowAddedToCart] = useState(false);
  const [showMoreBadges, setShowMoreBadges] = useState(false);
  const [, setZoomImage] = useState(false);

  // Pre-order state
  const [isPreOrderEnabled, setIsPreOrderEnabled] = useState(false);
  const [preOrderEndDate, setPreOrderEndDate] = useState(null);
  const [preOrderPaymentMode, setPreOrderPaymentMode] = useState('');

  // Package state

  // Handle image errors and set default placeholder
  const handleImageError = (index) => (e) => {
    e.target.onerror = null;
    // Track which image failed
    setImageErrors(prev => ({ ...prev, [index]: true }));
    // Set placeholder image
    e.target.src = placeholderImage;
    // Add a class to style the placeholder differently if needed
    e.target.classList.add('placeholder-image');
  };

  // Helper to get safe image URL
  const getSafeImageUrl = (url) => {
    if (!url || url === '' || url === 'null' || url === 'undefined') {
      return placeholderImage;
    }
    return url;
  };

  // Check if product is completely sold out (no regular stock and no preorder stock)
  const isSoldOut = () => {
    if (!product?.pricing) return false;

    const now = new Date();
    return product.pricing.every(pricing => {
      const hasRegularStock = pricing.availableStock > 0;
      const preOrderEndDate = pricing.preOrderdateEnd ? new Date(pricing.preOrderdateEnd) : null;
      const isPreOrderActive = preOrderEndDate && now <= preOrderEndDate;
      const hasPreOrderStock = pricing.isPreOrderEnabled && isPreOrderActive && pricing.preOrderStockLeft > 0;

      return !hasRegularStock && !hasPreOrderStock;
    });
  };

  // Get pricing for variation key
  const getPricingForVariationKey = useCallback((key, pricingData) => {
    if (!pricingData || !key) {
      return;
    }

    const matchedPricing = pricingData.find((pricing) => {
      const normalizedPricingKey = pricing.variationKey.replace(/,\s*/g, ',').trim();
      const normalizedSearchKey = key.replace(/,\s*/g, ',').trim();
      return normalizedPricingKey === normalizedSearchKey;
    });

    if (matchedPricing) {
      setVariationId(matchedPricing._id);
      setPrice(matchedPricing.discountedPrice || matchedPricing.normalPrice);
      setNormalPrice(matchedPricing.normalPrice);
      setDiscountedPrice(matchedPricing.discountedPrice);

      // Check for pre-order availability
      const currentDate = new Date();
      const preOrderEndDate = matchedPricing.preOrderdateEnd ? new Date(matchedPricing.preOrderdateEnd) : null;
      const isPreOrderActive = preOrderEndDate && currentDate <= preOrderEndDate;

      if (matchedPricing.isPreOrderEnabled &&
          matchedPricing.preOrderStockLeft > 0 &&
          isPreOrderActive &&
          matchedPricing.availableStock <= 0) {
        // Pre-order is available
        setIsPreOrderEnabled(true);
        setPreOrderEndDate(matchedPricing.preOrderdateEnd);
        setPreOrderPaymentMode(matchedPricing.preOrderPayMethod);
        setStock(matchedPricing.preOrderStockLeft);
      } else if (matchedPricing.availableStock > 0) {
        // Regular stock is available
        setIsPreOrderEnabled(false);
        setStock(matchedPricing.availableStock);
      } else {
        // No stock available
        setIsPreOrderEnabled(false);
        setStock(0);
      }
    } else {
      setPrice(0);
      setNormalPrice(0);
      setDiscountedPrice(0);
      setStock(0);
      setVariationId('');
      setIsPreOrderEnabled(false);
    }
  }, []);

  // Handle variation selection
  const handleVariationSelect = useCallback((variationType, value) => {
    setVariationsError(false);
    setVariations(prev => {
      const updated = { ...prev, [variationType]: value };

      if (product?.variations) {
        const keyParts = product.variations.map(v => updated[v.variationName] || '').filter(v => v);
        const newKey = keyParts.join(', ');
        setVariationKey(newKey);

        if (keyParts.length === product.variations.length) {
          getPricingForVariationKey(newKey, product.pricing);
        } else {
          setPrice(0);
          setStock(0);
          setVariationId('');
        }
      }

      return updated;
    });
  }, [product, getPricingForVariationKey]);

  // Fetch product details
  const fetchProduct = useCallback(async () => {
    if (!id) return;

    // Set loading states immediately (synchronously)
    setLoading(true);
    setIsLoading(true);
    setError(null);

    try {
      // Add a minimum delay to ensure smooth loading experience
      const [response] = await Promise.all([
        productsApi.getById(id),
        new Promise(resolve => setTimeout(resolve, 800)) // Minimum 800ms loading
      ]);

      if (response.status === 200 && response.data) {
        const productData = response.data;

        // Map API data to template format
        const mappedProduct = {
          ...productData,
          id: productData._id || productData.productId,
          name: productData.productName,
          category: productData.productCategory,
          description: productData.productDescription,
          imageUrl: productData.featuredImage || placeholderImage,
          stock: 0, // Will be set based on variation selection
          price: 0, // Will be set based on variation selection
          originalPrice: 0, // Will be set based on variation selection
        };

        setProduct(mappedProduct);

        // Check if product is in wishlist for authenticated users
        const customerId = secureLocalStorage.getItem('customerId');
        if (customerId) {
          const checkResult = await profileApi.checkWishlistItem(customerId, mappedProduct.id);
          if (checkResult.success && checkResult.inWishlist) {
            setIsFavorite(true);
          }
        } else {
          // For non-authenticated users, check localStorage
          const savedWishlist = localStorage.getItem('wishlist');
          if (savedWishlist) {
            const wishlistItems = JSON.parse(savedWishlist);
            const isInWishlist = wishlistItems.some(item => (item.id || item._id) === mappedProduct.id);
            setIsFavorite(isInWishlist);
          }
        }

        // Set default variation
        if (productData.variations && productData.pricing && productData.pricing.length > 0) {
          if (productData.variations[0]?.variationName === 'default') {
            setVariations({ default: 'default' });
            setVariationKey('default');
            getPricingForVariationKey('default', productData.pricing);
          } else {
            let defaultPricing = productData.pricing.find(p => p.availableStock > 0);
            if (!defaultPricing) {
              defaultPricing = productData.pricing[0];
            }

            if (defaultPricing) {
              const keyParts = defaultPricing.variationKey.split(',').map(k => k.trim());
              const defaultVariations = {};

              productData.variations.forEach((variation, index) => {
                if (keyParts[index]) {
                  defaultVariations[variation.variationName] = keyParts[index];
                }
              });

              setVariations(defaultVariations);
              setVariationKey(defaultPricing.variationKey);
              getPricingForVariationKey(defaultPricing.variationKey, productData.pricing);
            }
          }
        } else if (productData.productPrice || productData.price) {
          // Simple product without variations - set price and stock directly
          const simplePrice = productData.productPrice || productData.price || 0;
          setPrice(simplePrice);
          setNormalPrice(simplePrice);
          setDiscountedPrice(simplePrice);
          setStock(productData.availableStock || productData.stock || 10); // Default to 10 if no stock info
        }

        if (productData._id) {
          personalizationService.trackProductView(productData._id);
        }
      } else {
        // Product not found - show a proper not-found state instead of fake data
        setProduct(null);
        setError('not-found');
        setPrice(0);
        setStock(0);
      }
    } catch (err) {
      console.error('Error fetching product:', err);
      setProduct(null);
      setError('fetch-error');
      setPrice(0);
      setStock(0);
    } finally {
      setLoading(false);
      setIsLoading(false);
      setInitialLoad(false);
    }
  }, [id, getPricingForVariationKey]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  // Load recommendations when product loads
  useEffect(() => {
    const loadRecommendations = async () => {
      try {
        setLoadingRecommendations(true);
        const result = await recommendationsService.loadRecommendedProducts(1, 8);

        if (result.success) {
          // Ensure we have valid product data
          const validProducts = (result.data || []).filter(p => p && p.id);
          setRecommendedProducts(validProducts);
        } else {
          console.warn('Failed to load recommendations:', result.error);
          setRecommendedProducts([]);
        }
      } catch (error) {
        console.error('Error loading recommendations:', error);
        setRecommendedProducts([]);
        // Silently fail - recommendations are nice to have but not critical
      } finally {
        setLoadingRecommendations(false);
      }
    };

    if (product && product.id) {
      loadRecommendations();
    }
  }, [product]);

  // Get product images with proper placeholder handling
  const productImages = product ? [
    getSafeImageUrl(product.featuredImage || product.imageUrl),
    ...(product.additionalImages || [])
      .filter(img => img)
      .map(img => getSafeImageUrl(img.image || img))
  ] : [placeholderImage];

  // Check if this is a simple product with default variation
  const isDefaultProduct = product?.variations?.[0]?.variationName === 'default';

  // Handle additional services selection (multiple)
  const handleSelectAdditionalServicesMultiple = (service, item, isChecked) => {
    setSelectedAdditionalServices(prevState => {
      const serviceIndex = prevState.findIndex(prevService => prevService.serviceId === service.serviceId);
      if (isChecked) {
        if (serviceIndex > -1) {
          const updatedServices = [...prevState];
          updatedServices[serviceIndex] = {
            ...updatedServices[serviceIndex],
            items: [
              ...updatedServices[serviceIndex].items,
              { itemId: item._id, itemName: item.itemName, itemPrice: item.itemPrice }
            ]
          };
          return updatedServices;
        } else {
          return [
            ...prevState,
            {
              serviceId: service.serviceId,
              serviceName: service.serviceName,
              items: [{ itemId: item._id, itemName: item.itemName, itemPrice: item.itemPrice }]
            }
          ];
        }
      } else {
        if (serviceIndex > -1) {
          const updatedServices = [...prevState];
          updatedServices[serviceIndex] = {
            ...updatedServices[serviceIndex],
            items: updatedServices[serviceIndex].items.filter(serviceItem => serviceItem.itemId !== item._id)
          };
          if (updatedServices[serviceIndex].items.length === 0) {
            updatedServices.splice(serviceIndex, 1);
          }
          return updatedServices;
        }
      }
      return prevState;
    });
  };

  // Handle additional services selection (single)
  const handleSelectAdditionalServicesSingle = (service, item) => {
    setSelectedAdditionalServices(prevState => {
      const updatedServices = prevState.map(prevService => {
        if (prevService.serviceId === service.serviceId) {
          return {
            ...prevService,
            items: [{ itemId: item._id, itemName: item.itemName, itemPrice: item.itemPrice }]
          };
        }
        return prevService;
      });

      if (!updatedServices.some(prevService => prevService.serviceId === service.serviceId)) {
        updatedServices.push({
          serviceId: service.serviceId,
          serviceName: service.serviceName,
          items: [{ itemId: item._id, itemName: item.itemName, itemPrice: item.itemPrice }]
        });
      }
      return updatedServices;
    });
  };

  // Calculate additional services amount
  const calculateAdditionServiceAmount = useCallback(() => {
    const totalPriceSum = selectedAdditionalServices.reduce((total, service) => {
      return total + service.items.reduce((subtotal, item) => subtotal + item.itemPrice, 0);
    }, 0);
    return totalPriceSum;
  }, [selectedAdditionalServices]);

  // Automatically update additional services amount when services change
  useEffect(() => {
    const newTotal = calculateAdditionServiceAmount();
    setAdditionalServicesAmount(newTotal);
  }, [selectedAdditionalServices, calculateAdditionServiceAmount]);

  // Handle customize click for additional services
  const handleCustomizeClick = (serviceId, itemId, itemName) => {
    const inputValue = document.getElementById(`custom-value-${itemId}`)?.value || '';

    if (inputValue.trim()) {
      // Save the value
      setCustomizationValues(prev => ({
        ...prev,
        [itemId]: inputValue
      }));

      // Exit edit mode
      setCustomizationEditMode(prev => ({
        ...prev,
        [itemId]: false
      }));

      // Update the selected services with the custom value
      setSelectedAdditionalServices(prevState => {
        return prevState.map(service => {
          if (service.serviceId === serviceId) {
            return {
              ...service,
              items: service.items.map(item => {
                if (item.itemId === itemId) {
                  return {
                    ...item,
                    isCustomizableValue: inputValue
                  };
                }
                return item;
              })
            };
          }
          return service;
        });
      });
    }
  };

  // Handle edit button click
  const handleEditCustomization = (itemId) => {
    setCustomizationEditMode(prev => ({
      ...prev,
      [itemId]: true
    }));
  };

  // Remove a variant
  const handleRemoveVariant = (variantId) => {
    setProductVariants(productVariants.filter(v => v.id !== variantId));
  };
  
  // Add all variants to cart
  const handleAddAllToCart = async () => {
    if (productVariants.length === 0) {
      // If no variants, add current selection
      await handleAddSingleToCart();
    } else {
      // Calculate total quantity for package assignment
      let totalItems = 0;
      productVariants.forEach(variant => {
        totalItems += variant.quantity;
      });

      // Assign package based on total quantity
      const packageResult = await packageService.assignPackage(totalItems, dispatch);

      if (!packageResult.success && packageResult.error) {
        // Show error if package assignment failed critically
        setSnackbarMessage(packageResult.error);
        setSnackbarOpen(true);
        return;
      }

      // Add all variants to cart
      productVariants.forEach(variant => {
        const cartItem = {
          ...product,
          selectedSize: variant.size,
          selectedColor: variant.color,
          quantity: variant.quantity,
          personalization: variant.personalization,
          badge: variant.badge,
          additionalServices: variant.additionalServices,
          additionalServicesAmount: variant.additionalServicesAmount,
          isPreOrder: isPreOrderEnabled,
          preOrderEndDate: preOrderEndDate,
          preOrderPaymentMode: preOrderPaymentMode,
        };
        for (let i = 0; i < variant.quantity; i++) {
          cartApi.addToCart(cartItem);
        }
      });
      setShowAddedToCart(true);
      // Show success message with total items
      setSnackbarMessage(
        totalItems === 1
          ? `🎉 Great choice! ${productVariants[0]?.product.name} added to cart!`
          : `🛒 Awesome! ${totalItems} items added to your cart!`
      );
      setSnackbarOpen(true);
      setProductVariants([]); // Clear variants after adding
      setShowVariantsSummary(false);
    }
  };
  
  // Post cart item to API
  const postCartItem = async (payload) => {
    try {
      const businessNumber = secureLocalStorage.getItem('businessId') || '';
      const customerId = secureLocalStorage.getItem('customerId') || '';

      await productsApi.saveCartItem({
        ...payload,
        businessNo: businessNumber,
        userId: customerId,
      });
    } catch (error) {
      console.error('Error saving cart item:', error);
    }
  };

  // Calculate total price with customizations
  const calculateTotalPrice = () => {
    let basePrice = price || 0;
    let totalPrice = basePrice * quantity;

    // Add additional services costs (includes all customizations, badges, personalization, etc.)
    totalPrice += additionalServicesAmount * quantity;

    return totalPrice;
  };

  // Add single item (original function renamed)
  const handleAddSingleToCart = async () => {
    // Check if variations are selected for non-default products
    if (!isDefaultProduct && product?.variations) {
      const allVariationsSelected = product.variations.every(v => variations[v.variationName]);
      if (!allVariationsSelected) {
        setSnackbarMessage('Please select all required variations');
        setSnackbarOpen(true);
        setVariationsError(true);
        return;
      }
    }

    // Check stock
    if (stock === 0) {
      setSnackbarMessage('This item is out of stock');
      setSnackbarOpen(true);
      return;
    }

    // Assign package based on quantity
    const packageResult = await packageService.assignPackage(quantity, dispatch);

    if (!packageResult.success && packageResult.error) {
      // Show error if package assignment failed critically (e.g., out of stock)
      setSnackbarMessage(packageResult.error);
      setSnackbarOpen(true);
      return;
    }

    // Create cart payload with all customizations (now all handled via additional services)
    const payload = {
      productId: product._id || product.productId,
      productName: product.productName || product.name,
      productPrice: price,
      normalPrice: normalPrice,
      discountedPrice: discountedPrice,
      quantity: quantity,
      variationId: variationId,
      variationKey: variationKey,
      selectedVariations: variations, // Store selected variations for cart display
      variations: variations,
      additionalServices: selectedAdditionalServices, // This now includes all customizations
      additionalServicesAmount: additionalServicesAmount,
      isPreOrder: isPreOrderEnabled,
      preOrderEndDate: preOrderEndDate,
      preOrderPaymentMode: preOrderPaymentMode,
      totalPrice: calculateTotalPrice(),
      image: product.featuredImage || product.imageUrl,
    };

    // Dispatch to Redux store if package was assigned successfully
    if (packageResult.success) {
      dispatch(addToCart(payload));

      // Post to API
      postCartItem(payload);
    }

    // Update local cart with the product
    // Create a clean cart item with product data and selected variations/services
    const cartItem = {
      // Product base info
      id: product._id || product.productId,
      _id: product._id,
      productId: product._id || product.productId,
      name: product.productName || product.name,
      productName: product.productName || product.name,
      category: product.productCategory || product.category,
      imageUrl: product.featuredImage || product.imageUrl || product.image,
      price: price,
      normalPrice: normalPrice,
      discountedPrice: discountedPrice,

      // Variation info
      variationId: variationId,
      variationKey: variationKey,
      selectedVariations: variations, // The selected variation values (e.g., {COLOR: BLACK, RAM: 2GB})

      // Additional services
      selectedAdditionalServices: selectedAdditionalServices,
      additionalServicesAmount: additionalServicesAmount,

      // Other cart details
      quantity: 1, // Each item added once, quantity controlled by loop
      isPreOrder: isPreOrderEnabled,
      preOrderEndDate: preOrderEndDate,
      preOrderPaymentMode: preOrderPaymentMode,
    };

    for (let i = 0; i < quantity; i++) {
      cartApi.addToCart(cartItem);
    }

    setShowAddedToCart(true);
    // Show success message
    setSnackbarMessage(
      quantity === 1
        ? `🎉 Great choice! ${product.name} added to cart!`
        : `🛒 Awesome! ${quantity} ${product.name} added to your cart!`
    );
    setSnackbarOpen(true);
  };
  
  const handleAddToCart = handleAddAllToCart;

  const handleQuantityChange = (delta) => {
    const newQuantity = quantity + delta;
    if (newQuantity >= 1 && newQuantity <= stock) {
      setQuantity(newQuantity);
    }
  };

  const discountPercentage = normalPrice && discountedPrice && normalPrice > discountedPrice
    ? Math.round(((normalPrice - discountedPrice) / normalPrice) * 100)
    : 0;

  // Always show skeleton when we don't have product data yet
  // This ensures skeleton shows immediately on mount
  const showSkeleton = (!product && !error) || loading || isLoading || initialLoad;

  // Render skeleton immediately without any transitions
  if (showSkeleton) {
    return (
      <Box sx={{
        backgroundColor: '#f8f9fa',
        minHeight: '100vh',
        width: '100%'
      }}>
        <Container maxWidth="lg" sx={{
          py: { xs: 2, sm: 3, md: 4 },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'white'
        }}>
          {/* Breadcrumbs Skeleton */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: 'color-mix(in srgb, var(--primary) 3%, transparent)',
              borderRadius: 2,
              display: { xs: 'none', sm: 'block' }
            }}
          >
            <Skeleton variant="text" width={200} height={24} animation="wave" />
          </Paper>

        <Grid container spacing={4} sx={{ flex: 1 }}>
          {/* Image Gallery Skeleton */}
          <Grid item xs={12} md={6}>
            <Box>
              {/* Main Image Skeleton with Animation */}
              <Box sx={{
                position: 'relative',
                borderRadius: 2,
                overflow: 'hidden',
                mb: 2
              }}>
                <Skeleton
                  variant="rectangular"
                  height={600}
                  animation="pulse"
                  sx={{
                    borderRadius: 2,
                    background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                    backgroundSize: '200% 100%',
                    animation: `${shimmer} 1.5s infinite`
                  }}
                />
                {/* Zoom Icon Placeholder */}
                <Box sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.9)'
                }}>
                  <Skeleton variant="circular" width={40} height={40} />
                </Box>
              </Box>

              {/* Thumbnail Images Skeleton */}
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                {[1, 2, 3, 4].map((item) => (
                  <Skeleton
                    key={item}
                    variant="rectangular"
                    width={80}
                    height={80}
                    sx={{ borderRadius: 1 }}
                    animation="wave"
                  />
                ))}
              </Box>
            </Box>
          </Grid>

          {/* Product Info Skeleton */}
          <Grid item xs={12} md={6}>
            <Box>
              {/* Title and Category */}
              <Skeleton variant="text" width="60%" height={48} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="30%" height={24} sx={{ mb: 2 }} />

              {/* Price Box */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 3,
                  backgroundColor: 'color-mix(in srgb, var(--primary) 3%, transparent)',
                  borderRadius: 2,
                  border: '1px solid color-mix(in srgb, var(--primary) 10%, transparent)'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
                  <Skeleton variant="text" width={150} height={48} />
                  <Skeleton variant="text" width={100} height={32} />
                </Box>
              </Paper>

              {/* Stock Status */}
              <Skeleton variant="rectangular" height={50} sx={{ mb: 3, borderRadius: 2 }} />

              {/* Variations Section */}
              <Box sx={{ mb: 3 }}>
                <Skeleton variant="text" width={100} height={28} sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {[1, 2, 3, 4, 5].map((item) => (
                    <Skeleton
                      key={item}
                      variant="rectangular"
                      width={65}
                      height={48}
                      sx={{ borderRadius: 1 }}
                    />
                  ))}
                </Box>
              </Box>

              {/* Another Variation */}
              <Box sx={{ mb: 3 }}>
                <Skeleton variant="text" width={100} height={28} sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {[1, 2, 3].map((item) => (
                    <Skeleton
                      key={item}
                      variant="circular"
                      width={50}
                      height={50}
                    />
                  ))}
                </Box>
              </Box>

              {/* Customization Options */}
              <Box sx={{ mb: 3 }}>
                <Skeleton variant="text" width={150} height={28} sx={{ mb: 2 }} />
                <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1 }} />
              </Box>

              {/* Quantity and Add to Cart */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 4 }}>
                <Skeleton variant="rectangular" width={120} height={48} sx={{ borderRadius: 1 }} />
                <Skeleton variant="rectangular" width={200} height={48} sx={{ borderRadius: 1 }} />
              </Box>

              {/* Description Section */}
              <Box>
                <Skeleton variant="text" width={120} height={32} sx={{ mb: 2 }} />
                <Skeleton variant="text" width="100%" />
                <Skeleton variant="text" width="100%" />
                <Skeleton variant="text" width="80%" />
              </Box>
            </Box>
          </Grid>
        </Grid>

        </Container>
      </Box>
    );
  }

  // No product and nothing loading - show a proper error/empty state
  if (error || !product) {
    return (
      <Box sx={{
        backgroundColor: '#f8f9fa',
        minHeight: '100vh',
        width: '100%'
      }}>
        <Container maxWidth="lg" sx={{
          py: { xs: 2, sm: 3, md: 4 },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'white',
          textAlign: 'center'
        }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            {error === 'fetch-error' ? 'Something went wrong' : 'Product Not Found'}
          </Typography>
          <Typography sx={{ color: '#666', mb: 4, maxWidth: 420 }}>
            {error === 'fetch-error'
              ? "We couldn't load this product. Please try again."
              : "We couldn't find the product you're looking for. It may have been removed or is no longer available."}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Button variant="outlined" onClick={fetchProduct}>
              Try again
            </Button>
            <Button variant="contained" onClick={() => navigate('/products')}>
              Browse products
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  const seoDescription = product.description
    ? product.description.slice(0, 150)
    : 'View product details, pricing and availability.';

  // Real product attributes for the details tab (no fabricated specs)
  const categoryName = typeof product.productCategory === 'string'
    ? product.productCategory
    : product.productCategory?.categoryName;
  const productSpecs = [
    categoryName && { label: 'Category', value: categoryName },
    ...(product.variations || [])
      .filter((v) => v?.variationName && v.variationName !== 'default')
      .map((v) => ({ label: v.variationName, value: (v.variationValues || []).join(', ') })),
  ].filter(Boolean);

  const shippingContent = parseDraftContent(disclaimer?.shippingPolicy);
  const returnsContent = parseDraftContent(disclaimer?.returnsAndExchange);
  const productTabs = [
    { key: 'description', label: 'Description' },
    ...(productSpecs.length > 0 ? [{ key: 'details', label: 'Product Details' }] : []),
    ...((shippingContent || returnsContent) ? [{ key: 'shipping', label: 'Shipping & Returns' }] : []),
  ];
  const activeTabKey = productTabs[Math.min(activeTab, productTabs.length - 1)]?.key || 'description';

  return (
    <Fade in={true} timeout={600}>
      <Box>
        <Seo title={product.name} description={seoDescription} />
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
        {/* Enhanced Breadcrumbs */}
        <Fade in={true} timeout={500}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2, 
              mb: 3,
              backgroundColor: 'color-mix(in srgb, var(--primary) 3%, transparent)',
              borderRadius: 2,
              display: { xs: 'none', sm: 'block' }
            }}
          >
            <Breadcrumbs sx={{ fontSize: '0.875rem' }}>
              <Link
                component="button"
                onClick={() => navigate('/')}
                sx={{ 
                  color: '#666', 
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  '&:hover': { color: 'var(--primary)' } 
                }}
              >
                Home
              </Link>
              <Link
                component="button"
                onClick={() => navigate('/products')}
                sx={{ color: '#666', textDecoration: 'none', '&:hover': { color: 'var(--primary)' } }}
              >
                Products
              </Link>
              <Link
                component="button"
                onClick={() => navigate(`/products?category=${product.category.toLowerCase().replace(' ', '-')}`)}
                sx={{ color: '#666', textDecoration: 'none', '&:hover': { color: 'var(--primary)' } }}
              >
                {product.category}
              </Link>
              <Typography color="text.primary" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                {product.name}
              </Typography>
            </Breadcrumbs>
          </Paper>
        </Fade>

        {/* Mobile Back Button */}
        <Box sx={{ display: { xs: 'block', sm: 'none' }, mb: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ color: '#666' }}
          >
            Back
          </Button>
        </Box>

        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
          {/* Enhanced Product Images */}
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              position: { xs: 'static', md: 'sticky' }, 
              top: { md: 100 }
            }}>
              {/* Main Image with Zoom */}
              <Zoom in={true} timeout={600}>
                <Paper
                  elevation={3}
                  sx={{
                    position: 'relative',
                    width: '100%',
                    paddingTop: '100%',
                    backgroundColor: '#fff',
                    overflow: 'hidden',
                    mb: 2,
                    borderRadius: 2,
                    cursor: 'zoom-in',
                    '&:hover .zoom-icon': {
                      opacity: 1,
                    }
                  }}
                  onClick={() => setZoomImage(true)}
                >
                  <Box
                    component="img"
                    src={productImages[selectedImage]}
                    alt={product.name}
                    onError={handleImageError(selectedImage)}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      p: 2,
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                      },
                      '&.placeholder-image': {
                        opacity: 0.6,
                        filter: 'grayscale(100%)',
                      }
                    }}
                  />
                  
                  {/* Zoom Icon */}
                  <IconButton
                    className="zoom-icon"
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      backgroundColor: 'white',
                      opacity: 0,
                      transition: 'opacity 0.3s',
                      boxShadow: 2,
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                      }
                    }}
                  >
                    <ZoomInIcon />
                  </IconButton>

                  {/* Discount Badge */}
                  {discountPercentage > 0 && (
                    <Chip
                      label={`-${discountPercentage}%`}
                      sx={{
                        position: 'absolute',
                        top: 16,
                        left: 16,
                        backgroundColor: '#d32f2f',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        height: 36,
                        borderRadius: '18px',
                        animation: 'pulse 2s infinite',
                        '@keyframes pulse': {
                          '0%': { transform: 'scale(1)' },
                          '50%': { transform: 'scale(1.05)' },
                          '100%': { transform: 'scale(1)' },
                        }
                      }}
                    />
                  )}

                  {/* Pre-Order Badge - Template Styled */}
                  {isPreOrderEnabled && !isSoldOut() && (
                    <Chip
                      label="PRE-ORDER"
                      sx={{
                        position: 'absolute',
                        top: { xs: 12, sm: 16 },
                        right: { xs: 12, sm: 16 },
                        backgroundColor: '#F59E0B',        // Warning Orange from template
                        color: '#FFFFFF',                   // White text
                        fontWeight: 600,                    // Semibold
                        fontSize: { xs: '0.7rem', sm: '0.75rem' },  // 12px
                        height: { xs: 28, sm: 32 },
                        borderRadius: '9999px',             // Pill shape (fully rounded)
                        zIndex: 2,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    />
                  )}

                  {/* Sold Out Badge - Template Styled */}
                  {isSoldOut() && (
                    <Chip
                      label="SOLD OUT"
                      sx={{
                        position: 'absolute',
                        top: { xs: 12, sm: 16 },
                        right: { xs: 12, sm: 16 },
                        backgroundColor: '#795548',        // Brown from template
                        color: '#FFFFFF',                   // White text
                        fontWeight: 600,                    // Semibold
                        fontSize: { xs: '0.7rem', sm: '0.75rem' },  // 12px
                        height: { xs: 28, sm: 32 },
                        borderRadius: '9999px',             // Pill shape (fully rounded)
                        zIndex: 2,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    />
                  )}
                </Paper>
              </Zoom>

              {/* Enhanced Thumbnail Images */}
              <Box sx={{ 
                display: 'flex', 
                gap: 1,
                overflowX: 'auto',
                pb: 1,
                '&::-webkit-scrollbar': {
                  height: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f1f1f1',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'var(--primary)',
                  borderRadius: '3px',
                },
              }}>
                {productImages.map((image, index) => (
                  <Paper
                    key={index}
                    elevation={selectedImage === index ? 3 : 1}
                    onClick={() => setSelectedImage(index)}
                    sx={{
                      width: 80,
                      height: 80,
                      minWidth: 80,
                      border: selectedImage === index ? '2px solid var(--primary)' : '2px solid transparent',
                      cursor: 'pointer',
                      overflow: 'hidden',
                      transition: 'all 0.3s',
                      borderRadius: 1,
                      '&:hover': {
                        borderColor: 'var(--primary)',
                        transform: 'translateY(-2px)',
                        boxShadow: 3,
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={image}
                      alt={`${product.name} ${index + 1}`}
                      onError={handleImageError(index)}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        p: 0.5,
                        '&.placeholder-image': {
                          opacity: 0.6,
                          filter: 'grayscale(100%)',
                        }
                      }}
                    />
                  </Paper>
                ))}
              </Box>
            </Box>
          </Grid>

          {/* Enhanced Product Info */}
          <Grid item xs={12} md={6}>
            <Fade in={true} timeout={800}>
              <Box>
                {/* Header Section */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography 
                        variant="h4" 
                        sx={{ 
                          fontWeight: 700, 
                          fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
                          lineHeight: 1.2,
                          mb: 1
                        }}
                      >
                        {product.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip 
                          label={product.category} 
                          size="small"
                          sx={{ 
                            backgroundColor: 'color-mix(in srgb, var(--primary) 10%, transparent)',
                            color: 'var(--primary)',
                            fontWeight: 500
                          }}
                        />
                        {product.featured && (
                          <Chip 
                            label="FEATURED" 
                            size="small"
                            sx={{ 
                              backgroundColor: '#FFD700',
                              color: '#000',
                              fontWeight: 600
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        onClick={async () => {
                          const customerId = secureLocalStorage.getItem('customerId');
                          if (!customerId) {
                            setSnackbarMessage('Please log in to add items to wishlist');
                            setSnackbarOpen(true);
                            return;
                          }

                          if (isFavorite) {
                            // Remove from wishlist
                            const result = await accountService.removeFromWishlist(product?._id || id);
                            if (result.success) {
                              setIsFavorite(false);
                              setSnackbarMessage('Removed from wishlist');
                              setSnackbarOpen(true);
                            } else {
                              // Show error but don't change button state
                              setSnackbarMessage(result.error || 'Failed to remove from wishlist');
                              setSnackbarOpen(true);
                            }
                          } else {
                            // Add to wishlist
                            const result = await accountService.addToWishlist(product?._id || id);
                            if (result.success) {
                              // Only set to favorite on successful addition
                              setIsFavorite(true);
                              setSnackbarMessage('Added to wishlist');
                              setSnackbarOpen(true);
                            } else if (result.errorType === 'duplicate') {
                              // Product is already in wishlist - show info and set favorite state
                              setSnackbarMessage('Already in your wishlist');
                              setSnackbarOpen(true);
                              setIsFavorite(true);
                            } else {
                              // Other errors - show error message but don't change button state
                              setSnackbarMessage(result.error || 'Failed to add to wishlist');
                              setSnackbarOpen(true);
                              // Don't set isFavorite to true for errors
                            }
                          }
                        }}
                        sx={{
                          border: '1px solid',
                          // Only show red border/background if actually in wishlist (isFavorite is true)
                          borderColor: isFavorite ? '#d32f2f' : '#e0e0e0',
                          backgroundColor: isFavorite ? 'rgba(211, 47, 47, 0.05)' : 'transparent',
                          transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            borderColor: '#d32f2f',
                            backgroundColor: 'rgba(211, 47, 47, 0.1)',
                          },
                        }}
                      >
                        {isFavorite ?
                          <FavoriteFilledIcon sx={{ color: '#d32f2f' }} /> :
                          <FavoriteIcon sx={{ color: '#666' }} />
                        }
                      </IconButton>
                      <IconButton
                        sx={{
                          border: '1px solid #e0e0e0',
                          '&:hover': { borderColor: 'var(--primary)' },
                        }}
                      >
                        <ShareIcon sx={{ color: '#666' }} />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Enhanced Price Section */}
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 2.5,
                      mb: 3,
                      backgroundColor: 'color-mix(in srgb, var(--primary) 3%, transparent)',
                      borderRadius: 2,
                      border: '1px solid color-mix(in srgb, var(--primary) 10%, transparent)'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 1 }}>
                      <Typography variant="h3" sx={{
                        fontWeight: 700,
                        color: '#000',
                        fontSize: { xs: '2rem', sm: '2.5rem' }
                      }}>
                        KSH {((price * quantity) + (additionalServicesAmount * quantity)).toLocaleString()}
                      </Typography>
                      {normalPrice && normalPrice > price && (
                        <>
                          <Typography
                            sx={{
                              fontSize: '1.25rem',
                              color: '#999',
                              textDecoration: 'line-through',
                            }}
                          >
                            KSH {((normalPrice * quantity) + (additionalServicesAmount * quantity)).toLocaleString()}
                          </Typography>
                        </>
                      )}
                    </Box>

                    {/* Show price breakdown when additional services are selected */}
                    {additionalServicesAmount > 0 && (
                      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography sx={{ fontSize: '0.9rem', color: '#666' }}>
                            Product Price:
                          </Typography>
                          <Typography sx={{ fontSize: '0.9rem', color: '#666' }}>
                            KSH {(price * quantity).toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography sx={{ fontSize: '0.9rem', color: '#666' }}>
                            Additional Services:
                          </Typography>
                          <Typography sx={{ fontSize: '0.9rem', color: 'var(--primary)', fontWeight: 600 }}>
                            + KSH {(additionalServicesAmount * quantity).toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px dashed rgba(0, 0, 0, 0.1)' }}>
                          <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>
                            Total:
                          </Typography>
                          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#000' }}>
                            KSH {((price * quantity) + (additionalServicesAmount * quantity)).toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Paper>

                  {/* Stock Status */}
                  <Box sx={{ mb: 3 }}>
                    {isPreOrderEnabled ? (
                      <Alert
                        severity="warning"
                        sx={{ borderRadius: 2 }}
                      >
                        <Typography sx={{ fontWeight: 600, mb: 1 }}>
                          Pre-Order Item
                        </Typography>
                        <Typography sx={{ fontSize: '0.875rem' }}>
                          • Available for pre-order until {new Date(preOrderEndDate).toLocaleDateString()}<br />
                          • {stock} pre-order slots available<br />
                          • Payment method: {preOrderPaymentMode === 'onPlaceOrder' ? 'On Placing Order' : 'On Delivery'}
                        </Typography>
                      </Alert>
                    ) : stock > 0 ? (
                      <Alert
                        severity="info"
                        icon={<InfoIcon />}
                        sx={{ borderRadius: 2 }}
                      >
                        <Typography sx={{ fontWeight: 500 }}>
                          {stock < 10 ?
                            `Only ${stock} left in stock - order soon!` :
                            'In Stock - Ready to ship'
                          }
                        </Typography>
                      </Alert>
                    ) : isPreOrderEnabled ? (
                      <Alert severity="warning" sx={{ borderRadius: 2 }}>
                        Available for Pre-Order - Limited Stock
                      </Alert>
                    ) : (
                      <Alert severity="error" sx={{ borderRadius: 2 }}>
                        Out of Stock
                      </Alert>
                    )}
                  </Box>

                  {/* Dynamic Variation Selection - Only show for products with actual variations */}
                  {!isDefaultProduct && product?.variations && product.variations.map((variation) => (
                    <Box key={variation.variationName} sx={{ mb: 3 }}>
                      <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '1rem' }}>
                          {variation.variationName}: <span style={{ color: 'var(--primary)' }}>
                            {variations[variation.variationName] || 'Select'}
                          </span>
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        {variation.variationValues?.map((value) => (
                          <Button
                            key={value}
                            variant={variations[variation.variationName] === value ? 'contained' : 'outlined'}
                            onClick={() => handleVariationSelect(variation.variationName, value)}
                            sx={{
                              minWidth: '65px',
                              height: '48px',
                              borderColor: '#e0e0e0',
                              borderWidth: 2,
                              borderRadius: '6px',
                              color: variations[variation.variationName] === value ? 'white' : '#333',
                              backgroundColor: variations[variation.variationName] === value ? 'var(--primary)' : 'white',
                              fontWeight: 600,
                              '&:hover': {
                                borderColor: 'var(--primary)',
                                borderWidth: 2,
                                backgroundColor: variations[variation.variationName] === value ? 'var(--button-hover)' : 'color-mix(in srgb, var(--primary) 5%, transparent)',
                              },
                            }}
                          >
                            {value}
                          </Button>
                        ))}
                      </Box>
                    </Box>
                  ))}

                  {/* Badge Selection - DEPRECATED: Should be handled by Additional Services from API
                      Keep this for now for UI reference, but badges should come as an additional service */}
                  {false && <Box sx={{ mb: 3 }}>
                    <Typography sx={{ fontWeight: 600, mb: 1.5, fontSize: '1rem' }}>
                      Badge Options <Chip label="Optional +KSH 100" size="small" sx={{ ml: 1, fontSize: '0.7rem' }} />
                    </Typography>
                    
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 1 }}>
                      {/* No Badge Option */}
                      <Box
                        onClick={() => setSelectedBadge('')}
                        sx={{
                          p: 1.5,
                          border: '2px solid',
                          borderColor: selectedBadge === '' ? 'var(--primary)' : '#e0e0e0',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          textAlign: 'center',
                          backgroundColor: selectedBadge === '' ? 'color-mix(in srgb, var(--primary) 5%, transparent)' : 'white',
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: 'var(--primary)',
                            backgroundColor: 'color-mix(in srgb, var(--primary) 3%, transparent)',
                          },
                        }}
                      >
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>No Badge</Typography>
                      </Box>

                      {/* Badge Options */}
                      {[
                        { value: 'champions', label: 'Champions' },
                        { value: 'europa', label: 'Europa' },
                        ...(showMoreBadges ? [
                          { value: 'bundesliga', label: 'Bundesliga' },
                          { value: 'premierblue', label: 'Premier Blue' },
                          { value: 'seriea', label: 'Serie A' },
                          { value: 'laliga', label: 'La Liga' },
                          { value: 'mls', label: 'MLS' },
                          { value: 'conference', label: 'Conference' },
                          { value: 'carabao', label: 'Carabao Cup' },
                          { value: 'clubworldcup', label: 'Club World Cup' },
                        ] : [])
                      ].map((badge) => (
                        <Box
                          key={badge.value}
                          onClick={() => setSelectedBadge(badge.value)}
                          sx={{
                            p: 1.5,
                            border: '2px solid',
                            borderColor: selectedBadge === badge.value ? 'var(--primary)' : '#e0e0e0',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            textAlign: 'center',
                            backgroundColor: selectedBadge === badge.value ? 'color-mix(in srgb, var(--primary) 5%, transparent)' : 'white',
                            transition: 'all 0.2s',
                            position: 'relative',
                            '&:hover': {
                              borderColor: 'var(--primary)',
                              backgroundColor: 'color-mix(in srgb, var(--primary) 3%, transparent)',
                              transform: 'translateY(-1px)',
                            },
                          }}
                        >
                          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{badge.label}</Typography>
                          {selectedBadge === badge.value && (
                            <CheckCircleIcon 
                              sx={{ 
                                position: 'absolute', 
                                top: 4, 
                                right: 4, 
                                fontSize: 16, 
                                color: 'var(--primary)' 
                              }} 
                            />
                          )}
                        </Box>
                      ))}
                    </Box>

                    <Button
                      variant="text"
                      onClick={() => setShowMoreBadges(!showMoreBadges)}
                      size="small"
                      sx={{ 
                        mt: 1,
                        color: 'var(--primary)',
                        textTransform: 'none',
                        fontSize: '0.85rem',
                      }}
                    >
                      {showMoreBadges ? '− Show Less' : '+ Show 8 More Badges'}
                    </Button>
                  </Box>}

                  {/* Enhanced Custom Print Options - DEPRECATED: Should be handled by Additional Services from API
                      Keep this for now for UI reference, but personalization should come as additional services */}
                  {false && <Box sx={{ mb: 3 }}>
                    <Typography sx={{ fontWeight: 600, mb: 2, fontSize: '1rem' }}>
                      Personalization Options
                    </Typography>
                    
                    <Box sx={{ display: 'grid', gap: 2 }}>
                      <Box>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={enablePrintName}
                              onChange={(e) => {
                                setEnablePrintName(e.target.checked);
                                if (!e.target.checked) setCustomPrintName('');
                              }}
                              sx={{ color: 'var(--primary)', '&.Mui-checked': { color: 'var(--primary)' } }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Typography sx={{ fontWeight: 500 }}>PRINT NAME</Typography>
                              <Chip 
                                label="+ KSH 200" 
                                size="small"
                                sx={{ 
                                  backgroundColor: 'color-mix(in srgb, var(--primary) 10%, transparent)',
                                  color: 'var(--primary)',
                                  fontWeight: 600
                                }}
                              />
                            </Box>
                          }
                        />
                        {enablePrintName && (
                          <Fade in={enablePrintName}>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder="Enter name (e.g., SMITH)"
                              value={customPrintName}
                              onChange={(e) => setCustomPrintName(e.target.value.toUpperCase())}
                              sx={{ 
                                backgroundColor: 'white', 
                                mt: 1.5, 
                                ml: 4,
                                '& input': {
                                  fontWeight: 600,
                                  letterSpacing: 1
                                }
                              }}
                              inputProps={{ maxLength: 12 }}
                            />
                          </Fade>
                        )}
                      </Box>

                      <Box>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={enablePrintNumber}
                              onChange={(e) => {
                                setEnablePrintNumber(e.target.checked);
                                if (!e.target.checked) setCustomPrintNumber('');
                              }}
                              sx={{ color: 'var(--primary)', '&.Mui-checked': { color: 'var(--primary)' } }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Typography sx={{ fontWeight: 500 }}>PRINT NUMBER</Typography>
                              <Chip 
                                label="+ KSH 200" 
                                size="small"
                                sx={{ 
                                  backgroundColor: 'color-mix(in srgb, var(--primary) 10%, transparent)',
                                  color: 'var(--primary)',
                                  fontWeight: 600
                                }}
                              />
                            </Box>
                          }
                        />
                        {enablePrintNumber && (
                          <Fade in={enablePrintNumber}>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder="Enter number (1-99)"
                              value={customPrintNumber}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '');
                                if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 99)) {
                                  setCustomPrintNumber(value);
                                }
                              }}
                              sx={{ 
                                backgroundColor: 'white', 
                                mt: 1.5, 
                                ml: 4,
                                '& input': {
                                  fontWeight: 600,
                                  letterSpacing: 1
                                }
                              }}
                              inputProps={{ maxLength: 2 }}
                            />
                          </Fade>
                        )}
                      </Box>

                      {(enablePrintName || enablePrintNumber) && (
                        <Fade in={enablePrintName || enablePrintNumber}>
                          <Box>
                            <Typography sx={{ fontSize: '0.875rem', mb: 1.5, fontWeight: 600 }}>
                              Font Style
                            </Typography>
                            <RadioGroup
                              value={selectedFontType}
                              onChange={(e) => setSelectedFontType(e.target.value)}
                            >
                              <FormControlLabel
                                value="LEAGUE FONT"
                                control={<Radio sx={{ color: 'var(--primary)', '&.Mui-checked': { color: 'var(--primary)' } }} />}
                                label={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography>LEAGUE FONT</Typography>
                                    <Chip label="FREE" size="small" color="success" />
                                  </Box>
                                }
                              />
                              <FormControlLabel
                                value="TEAM FONT"
                                control={<Radio sx={{ color: 'var(--primary)', '&.Mui-checked': { color: 'var(--primary)' } }} />}
                                label={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography>TEAM FONT</Typography>
                                    <Chip label="FREE" size="small" color="success" />
                                  </Box>
                                }
                              />
                            </RadioGroup>
                          </Box>
                        </Fade>
                      )}
                    </Box>
                  </Box>}

                  {/* Additional Services Section - This now handles ALL customizations including badges, personalization, etc. */}
                  {product?.additionalServices && product.additionalServices.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography sx={{ fontWeight: 600, mb: 2, fontSize: '1rem' }}>
                        Additional Services
                      </Typography>

                      {product.additionalServices.map((service) => (
                        <Paper
                          key={service.serviceId}
                          elevation={0}
                          sx={{
                            p: { xs: 1.5, sm: 2, md: 2.5 },
                            mb: 2,
                            backgroundColor: '#fafafa',
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: '#f5f5f5',
                              borderColor: 'var(--primary)',
                              boxShadow: '0 2px 8px color-mix(in srgb, var(--primary) 10%, transparent)'
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                            <Typography sx={{ fontWeight: 600, color: 'var(--primary)' }}>
                              {service.serviceName}
                            </Typography>
                            <Chip
                              label={service.selectionType?.toLowerCase() === 'multiple' ? 'Select Multiple' : 'Select One'}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: '0.65rem',
                                height: '22px',
                                borderColor: service.selectionType?.toLowerCase() === 'multiple'
                                  ? '#4caf50'
                                  : 'var(--primary)',
                                backgroundColor: service.selectionType?.toLowerCase() === 'multiple'
                                  ? 'rgba(76, 175, 80, 0.05)'
                                  : 'color-mix(in srgb, var(--primary) 5%, transparent)',
                                color: service.selectionType?.toLowerCase() === 'multiple'
                                  ? '#4caf50'
                                  : 'var(--primary)',
                                '& .MuiChip-label': {
                                  px: 1,
                                  fontWeight: 500
                                }
                              }}
                            />
                          </Box>

                          {service.selectionType?.toLowerCase() === 'multiple' ? (
                            // Multiple selection - use checkboxes
                            <Box sx={{ display: 'grid', gap: 1 }}>
                              {service.items?.map((item) => {
                                const isSelected = selectedAdditionalServices.some(
                                  s => s.serviceId === service.serviceId &&
                                  s.items.some(i => i.itemId === item._id)
                                );

                                return (
                                  <Box key={item._id}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={isSelected}
                                          onChange={(e) => {
                                            handleSelectAdditionalServicesMultiple(service, item, e.target.checked);
                                          }}
                                          sx={{ color: 'var(--primary)', '&.Mui-checked': { color: 'var(--primary)' } }}
                                        />
                                      }
                                      label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                          <Typography sx={{ fontWeight: 500 }}>{item.itemName}</Typography>
                                          <Chip
                                            label={item.itemPrice === 0 ? 'FREE' : `+ KSH ${item.itemPrice.toLocaleString()}`}
                                            size="small"
                                            color={item.itemPrice === 0 ? 'success' : 'default'}
                                            sx={{
                                              backgroundColor: item.itemPrice === 0
                                                ? 'rgba(46, 125, 50, 0.1)'
                                                : 'color-mix(in srgb, var(--primary) 10%, transparent)',
                                              color: item.itemPrice === 0
                                                ? '#2e7d32'
                                                : 'var(--primary)',
                                              fontWeight: 600,
                                              fontSize: '0.75rem'
                                            }}
                                          />
                                        </Box>
                                      }
                                    />

                                  {item.isCustomizable && isSelected && (
                                    <Fade in={isSelected}>
                                      <Box sx={{
                                        ml: { xs: 2, sm: 3, md: 4 },
                                        mt: 1.5,
                                        p: { xs: 1, sm: 1.5 },
                                        backgroundColor: 'color-mix(in srgb, var(--primary) 3%, transparent)',
                                        borderRadius: 1,
                                        border: '1px solid color-mix(in srgb, var(--primary) 20%, transparent)'
                                      }}>
                                        {customizationValues[item._id] && !customizationEditMode[item._id] ? (
                                          // Show saved value with edit button
                                          <Box>
                                            <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                                              {item.itemName} customization:
                                            </Typography>
                                            <Box sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'space-between',
                                              mt: 0.5
                                            }}>
                                              <Typography sx={{
                                                fontWeight: 600,
                                                color: '#333',
                                                fontSize: '0.95rem'
                                              }}>
                                                {customizationValues[item._id]}
                                              </Typography>
                                              <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => handleEditCustomization(item._id)}
                                                sx={{
                                                  borderColor: 'var(--primary)',
                                                  color: 'var(--primary)',
                                                  textTransform: 'none',
                                                  minWidth: 'auto',
                                                  px: { xs: 1, sm: 1.5 },
                                                  '&:hover': {
                                                    borderColor: 'var(--button-hover)',
                                                    backgroundColor: 'color-mix(in srgb, var(--primary) 5%, transparent)'
                                                  }
                                                }}
                                              >
                                                Edit
                                              </Button>
                                            </Box>
                                          </Box>
                                        ) : (
                                          // Show input field for editing
                                          <Box>
                                            <Typography variant="caption" sx={{ color: '#666', mb: 1, display: 'block' }}>
                                              Customize {item.itemName}:
                                            </Typography>
                                            <Box sx={{
                                              display: 'flex',
                                              gap: { xs: 0.5, sm: 1 },
                                              flexDirection: { xs: 'column', sm: 'row' }
                                            }}>
                                              <TextField
                                                id={`custom-value-${item._id}`}
                                                size="small"
                                                placeholder={`Enter value`}
                                                defaultValue={customizationValues[item._id] || ''}
                                                sx={{
                                                  flex: { xs: 'auto', sm: 1 },
                                                  width: { xs: '100%', sm: 'auto' },
                                                  backgroundColor: 'white',
                                                  '& .MuiOutlinedInput-root': {
                                                    '&:hover fieldset': {
                                                      borderColor: 'var(--primary)',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                      borderColor: 'var(--primary)',
                                                    }
                                                  }
                                                }}
                                              />
                                              <Box sx={{
                                                display: 'flex',
                                                gap: 0.5,
                                                width: { xs: '100%', sm: 'auto' }
                                              }}>
                                                <Button
                                                  variant="contained"
                                                  size="small"
                                                  onClick={() => handleCustomizeClick(service.serviceId, item._id, item.itemName)}
                                                  sx={{
                                                    backgroundColor: 'var(--button-primary)',
                                                    color: 'white',
                                                    textTransform: 'none',
                                                    px: { xs: 1.5, sm: 2 },
                                                    py: { xs: 1, sm: 0.75 },
                                                    fontSize: { xs: '0.875rem', sm: '0.875rem' },
                                                    flex: { xs: 1, sm: 'initial' },
                                                    whiteSpace: 'nowrap',
                                                    '&:hover': {
                                                      backgroundColor: 'var(--button-hover)'
                                                    }
                                                  }}
                                                >
                                                  Save
                                                </Button>
                                                {customizationValues[item._id] && (
                                                  <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => {
                                                      setCustomizationEditMode(prev => ({
                                                        ...prev,
                                                        [item._id]: false
                                                      }));
                                                    }}
                                                    sx={{
                                                      borderColor: '#999',
                                                      color: '#666',
                                                      textTransform: 'none',
                                                      px: { xs: 1.5, sm: 2 },
                                                      py: { xs: 1, sm: 0.75 },
                                                      fontSize: { xs: '0.875rem', sm: '0.875rem' },
                                                      flex: { xs: 1, sm: 'initial' },
                                                      '&:hover': {
                                                        borderColor: '#666',
                                                        backgroundColor: 'rgba(0, 0, 0, 0.05)'
                                                      }
                                                    }}
                                                  >
                                                    Cancel
                                                  </Button>
                                                )}
                                              </Box>
                                            </Box>
                                          </Box>
                                        )}
                                      </Box>
                                    </Fade>
                                  )}
                                  </Box>
                                );
                              })}
                            </Box>
                          ) : (
                            // Single selection - use radio buttons in a group
                            <RadioGroup
                              value={
                                selectedAdditionalServices.find(s => s.serviceId === service.serviceId)?.items[0]?.itemId || ''
                              }
                              onChange={(e) => {
                                const selectedItem = service.items.find(item => item._id === e.target.value);
                                if (selectedItem) {
                                  handleSelectAdditionalServicesSingle(service, selectedItem);
                                }
                              }}
                            >
                              {service.items?.map((item) => {
                                const isSelected = selectedAdditionalServices.some(
                                  s => s.serviceId === service.serviceId &&
                                  s.items.some(i => i.itemId === item._id)
                                );

                                return (
                                  <Box key={item._id}>
                                    <FormControlLabel
                                      value={item._id}
                                      control={
                                        <Radio sx={{ color: 'var(--primary)', '&.Mui-checked': { color: 'var(--primary)' } }} />
                                      }
                                      label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                          <Typography sx={{ fontWeight: 500 }}>{item.itemName}</Typography>
                                          <Chip
                                            label={item.itemPrice === 0 ? 'FREE' : `+ KSH ${item.itemPrice.toLocaleString()}`}
                                            size="small"
                                            color={item.itemPrice === 0 ? 'success' : 'default'}
                                            sx={{
                                              backgroundColor: item.itemPrice === 0
                                                ? 'rgba(46, 125, 50, 0.1)'
                                                : 'color-mix(in srgb, var(--primary) 10%, transparent)',
                                              color: item.itemPrice === 0
                                                ? '#2e7d32'
                                                : 'var(--primary)',
                                              fontWeight: 600,
                                              fontSize: '0.75rem'
                                            }}
                                          />
                                        </Box>
                                      }
                                    />

                                    {item.isCustomizable && isSelected && (
                                      <Fade in={isSelected}>
                                        <Box sx={{
                                          ml: { xs: 2, sm: 3, md: 4 },
                                          mt: 1.5,
                                          p: { xs: 1, sm: 1.5 },
                                          backgroundColor: 'color-mix(in srgb, var(--primary) 3%, transparent)',
                                          borderRadius: 1,
                                          border: '1px solid color-mix(in srgb, var(--primary) 20%, transparent)'
                                        }}>
                                          {customizationValues[item._id] && !customizationEditMode[item._id] ? (
                                            // Show saved value with edit button
                                            <Box>
                                              <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                                                {item.itemName} customization:
                                              </Typography>
                                              <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                mt: 0.5
                                              }}>
                                                <Typography sx={{
                                                  fontWeight: 600,
                                                  color: '#333',
                                                  fontSize: '0.95rem'
                                                }}>
                                                  {customizationValues[item._id]}
                                                </Typography>
                                                <Button
                                                  variant="outlined"
                                                  size="small"
                                                  onClick={() => handleEditCustomization(item._id)}
                                                  sx={{
                                                    borderColor: 'var(--primary)',
                                                    color: 'var(--primary)',
                                                    textTransform: 'none',
                                                    minWidth: 'auto',
                                                    px: { xs: 1, sm: 1.5 },
                                                    '&:hover': {
                                                      borderColor: 'var(--button-hover)',
                                                      backgroundColor: 'color-mix(in srgb, var(--primary) 5%, transparent)'
                                                    }
                                                  }}
                                                >
                                                  Edit
                                                </Button>
                                              </Box>
                                            </Box>
                                          ) : (
                                            // Show input field for editing
                                            <Box>
                                              <Typography variant="caption" sx={{ color: '#666', mb: 1, display: 'block' }}>
                                                Customize {item.itemName}:
                                              </Typography>
                                              <Box sx={{
                                                display: 'flex',
                                                gap: { xs: 0.5, sm: 1 },
                                                flexDirection: { xs: 'column', sm: 'row' }
                                              }}>
                                                <TextField
                                                  id={`custom-value-${item._id}`}
                                                  size="small"
                                                  placeholder={`Enter value`}
                                                  defaultValue={customizationValues[item._id] || ''}
                                                  sx={{
                                                    flex: { xs: 'auto', sm: 1 },
                                                    width: { xs: '100%', sm: 'auto' },
                                                    backgroundColor: 'white',
                                                    '& .MuiOutlinedInput-root': {
                                                      '&:hover fieldset': {
                                                        borderColor: 'var(--primary)',
                                                      },
                                                      '&.Mui-focused fieldset': {
                                                        borderColor: 'var(--primary)',
                                                      }
                                                    }
                                                  }}
                                                />
                                                <Box sx={{
                                                  display: 'flex',
                                                  gap: 0.5,
                                                  width: { xs: '100%', sm: 'auto' }
                                                }}>
                                                  <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={() => handleCustomizeClick(service.serviceId, item._id, item.itemName)}
                                                    sx={{
                                                      backgroundColor: 'var(--button-primary)',
                                                      color: 'white',
                                                      textTransform: 'none',
                                                      px: { xs: 1.5, sm: 2 },
                                                      py: { xs: 1, sm: 0.75 },
                                                      fontSize: { xs: '0.875rem', sm: '0.875rem' },
                                                      flex: { xs: 1, sm: 'initial' },
                                                      whiteSpace: 'nowrap',
                                                      '&:hover': {
                                                        backgroundColor: 'var(--button-hover)'
                                                      }
                                                    }}
                                                  >
                                                    Save
                                                  </Button>
                                                  {customizationValues[item._id] && (
                                                    <Button
                                                      variant="outlined"
                                                      size="small"
                                                      onClick={() => {
                                                        setCustomizationEditMode(prev => ({
                                                          ...prev,
                                                          [item._id]: false
                                                        }));
                                                      }}
                                                      sx={{
                                                        borderColor: '#999',
                                                        color: '#666',
                                                        textTransform: 'none',
                                                        px: { xs: 1.5, sm: 2 },
                                                        py: { xs: 1, sm: 0.75 },
                                                        fontSize: { xs: '0.875rem', sm: '0.875rem' },
                                                        flex: { xs: 1, sm: 'initial' },
                                                        '&:hover': {
                                                          borderColor: '#666',
                                                          backgroundColor: 'rgba(0, 0, 0, 0.05)'
                                                        }
                                                      }}
                                                    >
                                                      Cancel
                                                    </Button>
                                                  )}
                                                </Box>
                                              </Box>
                                            </Box>
                                          )}
                                        </Box>
                                      </Fade>
                                    )}
                                  </Box>
                                );
                              })}
                            </RadioGroup>
                          )}
                        </Paper>
                      ))}

                      {additionalServicesAmount > 0 && (
                        <Paper
                          elevation={0}
                          sx={{
                            mt: 2,
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: 'color-mix(in srgb, var(--primary) 5%, transparent)',
                            border: '1px dashed',
                            borderColor: 'var(--primary)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <Typography sx={{ fontSize: '0.9rem', color: '#333', fontWeight: 500 }}>
                            Additional Services Total:
                          </Typography>
                          <Typography sx={{ fontSize: '1.1rem', color: 'var(--primary)', fontWeight: 700 }}>
                            + KSH {additionalServicesAmount.toLocaleString()}
                          </Typography>
                        </Paper>
                      )}
                    </Box>
                  )}

                  {/* Success Message */}
                  <Collapse in={showVariantAdded}>
                    <Alert 
                      severity="success" 
                      sx={{ mb: 2, borderRadius: '6px' }}
                      icon={<CheckCircleIcon />}
                    >
                      Variant saved! Configure another or click "Add All to Cart" when ready.
                    </Alert>
                  </Collapse>

                  {/* Variants Summary */}
                  {productVariants.length > 0 && (
                    <Collapse in={showVariantsSummary} timeout={500}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          mb: 3,
                          backgroundColor: 'color-mix(in srgb, var(--primary) 3%, transparent)',
                          borderRadius: 2,
                          border: '1px solid color-mix(in srgb, var(--primary) 10%, transparent)'
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BagIcon sx={{ color: 'var(--primary)', fontSize: 20 }} />
                            <Typography sx={{ fontWeight: 600, fontSize: '1rem' }}>
                              Ready to Add
                            </Typography>
                            <Chip 
                              label={`${productVariants.length} saved`} 
                              size="small" 
                              sx={{ backgroundColor: 'var(--primary)', color: 'white', fontWeight: 600, height: 22 }}
                            />
                          </Box>
                          <Typography sx={{ fontWeight: 600, color: 'var(--primary)' }}>
                            Total: KSH {productVariants.reduce((sum, v) => sum + v.totalPrice, 0).toLocaleString()}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'grid', gap: 1.5 }}>
                          {productVariants.map((variant, index) => (
                            <Paper
                              key={variant.id}
                              elevation={0}
                              sx={{
                                p: 1.5,
                                backgroundColor: 'white',
                                borderRadius: 1,
                                border: '1px solid #e0e0e0',
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ flex: 1 }}>
                                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 0.5 }}>
                                    Variant {index + 1}
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <Chip label={`Size: ${variant.size}`} size="small" />
                                    <Chip label={`Color: ${variant.color}`} size="small" />
                                    <Chip label={`Qty: ${variant.quantity}`} size="small" />
                                    {variant.personalization.printName && (
                                      <Chip label={`Name: ${variant.personalization.printName}`} size="small" color="primary" />
                                    )}
                                    {variant.personalization.printNumber && (
                                      <Chip label={`#${variant.personalization.printNumber}`} size="small" color="primary" />
                                    )}
                                    {variant.badge && (
                                      <Chip label={`Badge: ${variant.badge}`} size="small" />
                                    )}
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                                    KSH {variant.totalPrice.toLocaleString()}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveVariant(variant.id)}
                                    sx={{ color: '#d32f2f' }}
                                  >
                                    <RemoveIcon />
                                  </IconButton>
                                </Box>
                              </Box>
                            </Paper>
                          ))}
                        </Box>
                      </Paper>
                    </Collapse>
                  )}

                  {/* Enhanced Quantity and Add to Cart */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 2,
                      flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          border: '1px solid #e0e0e0',
                          borderRadius: '6px',
                          minWidth: { xs: '100%', sm: '150px' },
                          backgroundColor: 'white'
                        }}
                      >
                        <IconButton 
                          onClick={() => handleQuantityChange(-1)} 
                          disabled={quantity <= 1}
                          sx={{ 
                            borderRadius: '6px 0 0 6px',
                            borderRight: '1px solid #e0e0e0',
                            px: 2,
                            py: 1.5
                          }}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <Typography sx={{ 
                          flex: 1,
                          textAlign: 'center',
                          fontWeight: 600,
                          fontSize: '1.1rem',
                          px: 2
                        }}>
                          {quantity}
                        </Typography>
                        <IconButton
                          onClick={() => handleQuantityChange(1)}
                          disabled={quantity >= stock || stock === 0}
                          sx={{
                            borderRadius: '0 6px 6px 0',
                            borderLeft: '1px solid #e0e0e0',
                            px: 2,
                            py: 1.5
                          }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Box>

                      <Button
                        variant="contained"
                        size="medium"
                        onClick={handleAddToCart}
                        disabled={stock === 0 || (!isDefaultProduct && product?.variations && !product.variations.every(v => variations[v.variationName]))}
                        startIcon={<BagIcon />}
                        sx={{
                          minWidth: { xs: '100%', sm: '200px' },
                          maxWidth: { xs: '100%', sm: '300px' },
                          flex: { xs: 1, sm: 0 },
                          backgroundColor: 'var(--button-primary)',
                          py: 1,
                          fontSize: '1rem',
                          fontWeight: 600,
                          borderRadius: '6px',
                          textTransform: 'none',
                          whiteSpace: 'nowrap',
                          boxShadow: '0 2px 8px color-mix(in srgb, var(--primary) 25%, transparent)',
                          '&:hover': {
                            backgroundColor: 'var(--button-hover)',
                            boxShadow: '0 4px 12px color-mix(in srgb, var(--primary) 35%, transparent)',
                            transform: 'translateY(-1px)',
                          },
                          '&:active': {
                            transform: 'translateY(1px)',
                            boxShadow: '0 1px 4px color-mix(in srgb, var(--primary) 20%, transparent)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {stock === 0 && !isPreOrderEnabled ? 'Out of Stock' :
                         isPreOrderEnabled  ? 'Pre-Order Now' :
                         productVariants.length > 0 ? `Add All (${productVariants.reduce((sum, v) => sum + v.quantity, 0)} items)` :
                         'Add to Cart'}
                      </Button>

                    </Box>
                  </Box>

                </Box>
              </Box>
            </Fade>
          </Grid>
        </Grid>

        {/* Enhanced Product Tabs */}
        <Paper elevation={2} sx={{ mt: 6, borderRadius: 2, overflow: 'hidden' }}>
          <Tabs
            value={Math.min(activeTab, productTabs.length - 1)}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{
              backgroundColor: '#f8f8f8',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                minHeight: 56,
                '&.Mui-selected': {
                  color: 'var(--primary)',
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: 'var(--primary)',
                height: 3,
              }
            }}
            variant="scrollable"
            scrollButtons="auto"
          >
            {productTabs.map((tab) => (
              <Tab key={tab.key} label={tab.label} />
            ))}
          </Tabs>

          <Box sx={{ p: 4 }}>
            {activeTabKey === 'description' && (
              <Fade in={true}>
                <Box>
                  <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                    Product Description
                  </Typography>
                  <Typography sx={{ lineHeight: 1.8, color: '#333', mb: 2, whiteSpace: 'pre-line' }}>
                    {product.description || 'No description available for this product.'}
                  </Typography>
                </Box>
              </Fade>
            )}

            {activeTabKey === 'details' && (
              <Fade in={true}>
                <Box>
                  <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                    Product Details
                  </Typography>
                  <Grid container spacing={3}>
                    {productSpecs.map((spec, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            backgroundColor: '#f8f8f8',
                            borderRadius: 1
                          }}
                        >
                          <Typography sx={{ color: '#666', fontSize: '0.875rem', mb: 0.5, textTransform: 'capitalize' }}>
                            {spec.label.toLowerCase()}
                          </Typography>
                          <Typography sx={{ fontWeight: 500 }}>
                            {spec.value}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Fade>
            )}

            {activeTabKey === 'shipping' && (
              <Fade in={true}>
                <Box>
                  <Grid container spacing={4}>
                    {shippingContent && (
                      <Grid item xs={12} md={returnsContent ? 6 : 12}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                          Shipping & Delivery
                        </Typography>
                        <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f8f8f8' }}>
                          {shippingContent.blocks.map((block) => (
                            <Box key={block.key}>{RenderBlock(block, shippingContent)}</Box>
                          ))}
                        </Paper>
                      </Grid>
                    )}
                    {returnsContent && (
                      <Grid item xs={12} md={shippingContent ? 6 : 12}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                          Returns & Exchanges
                        </Typography>
                        <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f8f8f8' }}>
                          {returnsContent.blocks.map((block) => (
                            <Box key={block.key}>{RenderBlock(block, returnsContent)}</Box>
                          ))}
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Fade>
            )}
          </Box>
        </Paper>
      </Container>

      {/* Smart Recommendations Section */}
      <Container maxWidth="lg">
        <SmartRecommendations
          currentProduct={product}
          products={recommendedProducts}
          title="You May Also Like"
          showFilters={true}
          loading={loadingRecommendations}
        />
      </Container>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen || showAddedToCart}
        autoHideDuration={4000}
        onClose={() => {
          setSnackbarOpen(false);
          setShowAddedToCart(false);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => {
            setSnackbarOpen(false);
            setShowAddedToCart(false);
          }}
          severity="success" 
          sx={{ 
            width: '100%',
            '& .MuiAlert-message': {
              fontSize: '0.95rem',
              fontWeight: 500,
            }
          }}
          action={
            <Button 
              color="inherit" 
              size="small"
              onClick={() => navigate('/cart')}
              sx={{ 
                fontWeight: 600,
                fontSize: '0.8rem',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
              }}
            >
              VIEW CART
            </Button>
          }
        >
          {snackbarMessage || "Product added to cart successfully!"}
        </Alert>
      </Snackbar>
      </Box>
    </Fade>
  );
};

export default ProductDetail;